const OPTION_LABELS = ['a', 'b', 'c', 'd', 'e', 'f'];

function quote(value) {
  const text = String(value || '').trim();
  return text ? `"${text}"` : '';
}

function formatOptions(options) {
  if (!Array.isArray(options) || options.length === 0) return '';
  return options
    .map((opt, idx) => {
      const label = OPTION_LABELS[idx] || `${idx + 1}`;
      return `${label}) ${String(opt || '').trim()}`;
    })
    .join(' ');
}

function pushIf(list, condition, value) {
  const text = String(value || '').trim();
  if (condition && text) list.push(text);
}

function buildBaseContext({metadata, activeTerm}) {
  const lines = [];
  pushIf(lines, !!metadata?.topic, `Lesson Topic - ${metadata?.topic}`);
  pushIf(lines, metadata?.grade != null, `Grade - ${metadata?.grade}`);
  pushIf(lines, !!metadata?.subject, `Subject - ${metadata?.subject}`);
  pushIf(lines, !!activeTerm?.term, `Key Term - ${activeTerm?.term}`);
  pushIf(
    lines,
    !!activeTerm?.definition,
    `Key Term Definition - ${activeTerm?.definition}`,
  );
  return lines;
}

function getQuestionDetail(question) {
  if (!question) return '';
  const optionsText = formatOptions(question.options);
  if (optionsText) {
    return `Knowledge Check Question - ${question.question}, Options - ${optionsText}`;
  }
  if (Array.isArray(question.wordBank) && question.wordBank.length) {
    return `Knowledge Check Question - ${question.question}, Word Bank - ${question.wordBank.join(', ')}`;
  }
  if (Array.isArray(question.matchPairs) && question.matchPairs.length) {
    const pairs = question.matchPairs
      .map(pair => `${pair.left} ↔ ${pair.right}`)
      .join('; ');
    return `Knowledge Check Question - ${question.question}, Match Pairs - ${pairs}`;
  }
  return `Knowledge Check Question - ${question.question}`;
}

function getWorkbookDetail(workbook) {
  if (!workbook) return '';
  const optionsText = formatOptions(workbook.options);
  if (optionsText) {
    return `Exercise - ${workbook.exercise}, Options - ${optionsText}`;
  }
  return `Exercise - ${workbook.exercise || ''}`.trim();
}

function getLearningDetail(activeLearningStep) {
  if (!activeLearningStep) return '';
  const script = String(activeLearningStep.tutorScript || '').trim();
  if (!script) return '';
  return `Learning Step - ${script}`;
}

/**
 * Build the `currentContext` lines and the opening tutor greeting that the
 * chat panel uses when the user opens it. Per-phase rules:
 *   - learning:        "Hi! What can I help you with?"
 *   - workbook_*:      "...trying to solve a problem about \"{keyTerm}\"..."
 *   - knowledge_check: same as workbook, using the question's targetTerm.
 *                      If the question is cumulative AND targetTerm differs
 *                      from the active term, mention both.
 *   - other phases:    panel is not openable from those phases.
 */
export function buildTutorChatContext({
  phase,
  metadata,
  activeTerm,
  activeLearningStep,
  activeWorkbook,
  activeKCQuestion,
}) {
  const currentContext = buildBaseContext({metadata, activeTerm});

  switch (phase) {
    case 'learning':
      pushIf(currentContext, true, getLearningDetail(activeLearningStep));
      return {
        greeting: 'Hi! What can I help you with?',
        currentContext,
      };

    case 'workbook_ready':
    case 'workbook_exercise':
    case 'workbook_options': {
      pushIf(currentContext, true, getWorkbookDetail(activeWorkbook));
      const term = activeTerm?.term;
      const greeting = term
        ? `Hey! It looks like you're trying to solve a problem about ${quote(term)}. What can I help you with?`
        : 'Hi! What can I help you with?';
      return {greeting, currentContext};
    }

    case 'knowledge_check': {
      pushIf(currentContext, true, getQuestionDetail(activeKCQuestion));
      const targetTerm = String(activeKCQuestion?.targetTerm || '').trim();
      const activeTermName = String(activeTerm?.term || '').trim();
      const isCumulative = Boolean(activeKCQuestion?.isCumulative);

      let greeting;
      if (targetTerm && isCumulative && activeTermName && targetTerm !== activeTermName) {
        greeting = `Hey! It looks like you're trying to solve a problem about ${quote(targetTerm)} or ${quote(activeTermName)}. What can I help you with?`;
      } else {
        const term = targetTerm || activeTermName;
        greeting = term
          ? `Hey! It looks like you're trying to solve a problem about ${quote(term)}. What can I help you with?`
          : 'Hi! What can I help you with?';
      }

      return {greeting, currentContext};
    }

    default:
      return {
        greeting: 'Hi! What can I help you with?',
        currentContext,
      };
  }
}

export default buildTutorChatContext;
